import { z } from 'zod';

import { SupportActingAsSchema, SupportActorSchema } from '../support/SupportActor';
import { AppliedDiscountSchema } from './AppliedDiscount';
import { AppliedTaxSchema } from './AppliedTax';
import { OrderAttributionSchema } from '../guest_codes/GuestCode';
import { OrderItemSchema } from './OrderItem';
import { TaxExemptionSchema, WaivedTaxSchema } from './TaxExemption';
import { LateAutoGratuitySchema } from './LateGratuity';
import { OrderCardHoldSchema, OrderServerCloseStampSchema } from './CardHold';
import {
    ExternalOrderSourceSchema,
    KitchenStateSchema,
    OrderSourceSchema,
    OrderSplitStatusSchema,
    OrderStateSchema,
    OrderSyncPrioritySchema,
    OrderSyncStateSchema,
    OrderTypeSchema,
    OrderVoidInfoSchema,
} from './types';

/**
 * FIRE/HOLD per course for one order, keyed by `String(courseId)`.
 * Mirrors `IOrderCourseState` in packages/models/src/orders/Order.ts.
 *
 * Declared once and referenced by both OrderSchema and OrderDataSchema — two
 * inline copies is how the same field ends up validating differently depending
 * on which door the write came through.
 */
export const OrderCourseStatesSchema = z.record(
    z.string(),
    z.object({
        state: z.enum(['fire', 'hold']),
        firedAt: z.coerce.date().optional(),
        firedBy: z.string().optional(),
    }),
);

/**
 * Structured delivery address. Mirrors `IOrderDeliveryAddressComponents` in
 * packages/models/src/orders/Order.ts.
 *
 * Declared once: OrderSchema, OrderDataSchema AND CartSchema reference it. The
 * cart is where the guest types the address, so a cart schema without it
 * strips the field on every browser sync and the order never receives it.
 */
/**
 * POS service mode (`table` | `tab` | `takeout`) — see `@order-up/models`
 * `orders/serviceMode.ts`. Kept literal here so this package does not import
 * runtime values from models.
 */
export const OrderServiceModeSchema = z.enum(['table', 'tab', 'takeout']);

export const OrderDeliveryAddressComponentsSchema = z.object({
    line1: z.string().optional(),
    line2: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    postalCode: z.string().optional(),
    country: z.string().optional(),
});

export const OnlineServiceFeeLineSchema = z.object({
    amount: z.number().min(0),
    tax: z.number().min(0),
    label: z.string().min(1).max(40),
});

// SERVER-OWNED: checkout's per-order fee program stamp (docs/ONLINE_SERVICE_FEE.md).
// Declared because a plain `z.object()` STRIPS undeclared keys — a stamp the
// checkout set would be dropped by `OrderDataSchema.parse` and the order would
// never be billed.
export const OnlineOrderFeeProgramSchema = z.object({
    appliedAt: z.coerce.date(),
});

/**
 * Stamped by the server's offline projection when the `orderId` a till assigned
 * offline was already held by a DIFFERENT order, and the server re-minted it
 * (docs/GATEWAY_OFFLINE_COORDINATION_ARCHITECTURE.md §3). `fromOrderId` is the
 * till's colliding id; `conflictingOrderGuids` the orders that already held it.
 * The order's guid and its customer-facing `orderNumber` are never changed.
 */
export const OrderIdReassignmentSchema = z.object({
    fromOrderId: z.number().int().positive(),
    conflictingOrderGuids: z.array(z.string()),
    reason: z.literal('offline_projection_collision'),
    /** ISO-8601 instant of the re-mint. */
    reassignedAt: z.string(),
});

export const OrderSchema = z.object({
    docId: z.string().optional(),
    orderId: z.number().int().positive(),
    orderNumber: z.number().int().positive(),
    guid: z.string().min(1),
    restaurantId: z.number().int().positive(),
    tenantId: z.number().int().positive(),
    customerId: z.string().optional(),
    customerName: z.string().optional(),
    customerPhone: z.string().optional(),
    customerEmail: z.string().email().optional(),
    cartId: z.number().int().positive().optional(),
    items: z.array(OrderItemSchema).default([]),
    tableId: z.string().optional(),
    activeSeatCount: z.number().int().positive().optional(),
    courseStates: OrderCourseStatesSchema.optional(),
    type: OrderTypeSchema.default('dineIn'),
    orderLevelDiscounts: z.array(AppliedDiscountSchema).default([]),
    appliedTaxes: z.array(AppliedTaxSchema).default([]),
    // Order-level tax exemption (server-owned record) and the tax it waived.
    // Declared because a plain `z.object()` STRIPS undeclared keys — an order
    // parsed and written back would otherwise lose its exemption. See
    // docs/TAX_EXEMPTION_ARCHITECTURE.md.
    taxExemption: TaxExemptionSchema,
    lateAutoGratuity: LateAutoGratuitySchema,
    // Card hold projection + server close stamp (server-owned) — docs/CARD_HOLDS.md.
    cardHold: OrderCardHoldSchema,
    closedBy: OrderServerCloseStampSchema,
    closeReason: OrderServerCloseStampSchema,
    waivedTax: WaivedTaxSchema,
    scheduledTime: z.coerce.date().optional(),
    fireAt: z.coerce.date().optional(),
    // Who takes item stock off the shelf (models fireRules `serverTakesStockAtSend`).
    stockCommit: z.literal("atSend").optional(),
    // docs/DYNAMIC_PREP_TIME_ARCHITECTURE.md §5 — the persisted promise.
    promisedReadyAt: z.coerce.date().optional(),
    prepQuote: z
      .object({
        minutes: z.number(),
        paced: z.boolean(),
        channelBase: z.number(),
        orderBase: z.number(),
        largeOrderAdd: z.number(),
        loadItems: z.number(),
        loadAdd: z.number(),
        busyAdd: z.number(),
        scheduled: z.boolean(),
      })
      .optional(),
    deliveryAddress: z.string().optional(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
    createdByUserId: z.string().min(1),
    tabName: z.string().optional(),
    /** POS service mode — see IOrder.serviceMode. Absent = legacy; unknown = dropped, never a failed parse. */
    serviceMode: OrderServiceModeSchema.optional().catch(undefined),
    customerNotes: z.string().optional(),
    internalNotes: z.string().optional(),
    synced: z.boolean().default(false),
    canceledAt: z.coerce.date().optional(),
    canceledBy: z.string().optional(),
    voidedAt: z.coerce.date().optional(),
    voidedBy: z.string().optional(),

    // Three-axis order state (new)
    orderState: OrderStateSchema.default('draft'),
    kitchenState: KitchenStateSchema.default('none'),
    closedAt: z.coerce.date().optional(),
    // The reopen stamp (docs/PAYMENT_REVERSAL_ARCHITECTURE.md §Reopen on void).
    // Declared because a plain `z.object()` STRIPS undeclared keys: an API
    // round-trip would otherwise erase why a closed order is open again.
    reopenedAt: z.coerce.date().optional(),
    reopenReason: z.enum(['payment_voided']).optional(),
    reopenedByPaymentGuid: z.string().optional(),
    voidInfo: OrderVoidInfoSchema.optional(),
    isOverdue: z.boolean().optional(),

    // Offline-first fields
    syncState: OrderSyncStateSchema.default('localOnly'),
    syncPriority: OrderSyncPrioritySchema.default('normal'),
    lastSyncAttempt: z.coerce.date().optional(),
    syncRetryCount: z.number().int().min(0).default(0),
    startedAt: z.coerce.date().optional(),
    sentToKitchenAt: z.coerce.date().optional(),
    requiresFirestoreSync: z.boolean().default(false),
    isLocalOnly: z.boolean().default(false),

    // Employee tracking (harmonized with POS)
    createdByEmployeeId: z.number().int().positive().optional(),
    modifiedByEmployeeId: z.number().int().positive().optional(),
    sentToKitchenByEmployeeId: z.number().int().positive().optional(),
    completedByEmployeeId: z.number().int().positive().optional(),
    assignedEmployeeId: z.number().int().positive().optional(),
    assignedDeviceId: z.string().optional(),
    /**
     * Attribution for a write made by an OrderUp support session. Declared here
     * because a plain `z.object()` STRIPS undeclared keys — leaving it out would
     * delete the actor on every server round-trip while Firestore kept it.
     */
    supportActor: SupportActorSchema.optional(),
    /** The frozen act-as opener (see the model) — declared so a round-trip cannot strip it. */
    supportOpener: SupportActingAsSchema.optional(),
    // Segment-aware tip pooling: content-derived restaurantGuid:date:window id.
    segmentGuid: z.string().optional(),

    // Denormalized owning tenant/restaurant GUIDs — stamped at creation so
    // notification triggers (which forward to the ingest API resolving BY GUID)
    // never fall back to unresolvable docIds. See IOrder.tenantGuid JSDoc.
    tenantGuid: z.string().optional(),
    restaurantGuid: z.string().optional(),
    /**
     * WHICH BRAND the order was placed with. Declared here because an
     * undeclared property is STRIPPED before it is stored or returned — the
     * write would answer 200 with the attribution silently gone, which is the
     * exact failure this field exists to prevent. Optional: absent for
     * first-party orders and single-brand restaurants.
     */
    brandGuid: z.string().optional(),
    /** The brand's name as displayed at order time — see `IOrder.brandName`. */
    brandName: z.string().optional(),
    /** See `OrderIdReassignmentSchema`. Server-stamped; absent on almost every order. */
    orderIdReassignment: OrderIdReassignmentSchema.optional(),

    // Order source tracking
    orderSource: OrderSourceSchema.optional(),
    /** Set when a printed guest QR code sent the guest here. See IOrder.attribution JSDoc. */
    attribution: OrderAttributionSchema.optional(),
    externalOrderId: z.string().optional(),
    externalSource: ExternalOrderSourceSchema.optional(),
    /** DRIVE/COURIER external delivery id — set by DeliveryDispatchService on accept. See IOrder.externalDeliveryId. */
    externalDeliveryId: z.string().optional(),
    /** Courier provider for in-flight Drive delivery. See IOrder.courierProvider. */
    courierProvider: z
        // ⚠️ ADAPTER identity (D1), not marketplace attribution: this records who
        // we DISPATCH THROUGH. Must match `Restaurant.settings.courierProvider`
        // minus its `'none'` sentinel — an order that HAS a courier was
        // dispatched to one. Omitting `'kitchenhub'` made the only value an
        // aggregator dispatch writes the one value this enum rejected.
        .enum(['ubereats', 'doordash', 'grubhub', 'kitchenhub', 'uber_direct'])
        .optional(),
    /** Customer-facing delivery fee charged at checkout, in cents. See IOrder.customerDeliveryFeeCents. */
    customerDeliveryFeeCents: z.number().int().nonnegative().optional(),
    /** Merchant Drive courier cost in cents (server-side only). See IOrder.merchantDriveCostCents. */
    merchantDriveCostCents: z.number().int().nonnegative().optional(),
    /** Provider-neutral courier fields (Uber Direct spec 2026-10-27 §6.4). See IOrder JSDoc. */
    courierQuoteId: z.string().optional(),
    courierQuoteExpiresAt: z.string().optional(),
    merchantCourierCostCents: z.number().int().nonnegative().optional(),
    courierLocation: z.object({ lat: z.number(), lng: z.number() }).optional(),
    courierDispatchFailure: z
        .object({ reason: z.string(), detail: z.string().optional(), at: z.string(), notifiedAt: z.string().optional() })
        .optional(),
    /** Live courier tracking (Drive §7.2) — see IOrder JSDoc. */
    courierStatus: z.string().optional(),
    dasherLocation: z.object({ lat: z.number(), lng: z.number() }).optional(),
    eta: z.string().optional(),
    courierStatusUpdatedAt: z.string().optional(),
    trackingUrl: z.string().optional(),
    courierName: z.string().optional(),
    courierPhone: z.string().optional(),
    /** PIN for the courier's anonymized line (Uber `phone.pin_code`). */
    courierPhonePin: z.string().optional(),
    /** Dasher vehicle (make/model/year) from the courier-status webhook. */
    dasherVehicle: z
        .object({
            make: z.string().optional(),
            model: z.string().optional(),
            year: z.string().optional(),
        })
        .optional(),
    /** Structured dropoff address components for Drive §5.2 dispatch. */
    deliveryAddressComponents: OrderDeliveryAddressComponentsSchema.optional(),
    /** Synthetic management test order — see IOrder.isTest. */
    isTest: z.boolean().optional(),
    /** Canonical courier milestones already notified to the customer — see IOrder.notifiedCourierMilestones. */
    notifiedCourierMilestones: z.array(z.string()).optional(),

    // Check splitting & transfer fields — reference array (checkId or checkGuid)
    checks: z.array(z.union([z.string(), z.number()])).default([]),
    activeCheckCount: z.number().int().nonnegative().optional(),
    totalServiceCharges: z.number().min(0).default(0),
    totalServiceTax: z.number().min(0).default(0),
    onlineServiceFee: OnlineServiceFeeLineSchema.optional(),
    onlineOrderFeeProgram: OnlineOrderFeeProgramSchema.optional(),
    splitStatus: OrderSplitStatusSchema.default('unsplit'),
    hasReceivedTransfers: z.boolean().optional(),
    hasOutgoingTransfers: z.boolean().optional(),
    // Uber Eats marketplace-specific ingest fields — see IOrder JSDoc.
    /** Customer-side anonymized-line PIN (Uber `customers[].contact.phone.pin_code`). */
    customerPhonePin: z.string().optional(),
    /** Whether the customer requested cutlery / single-use items. */
    cutleryRequested: z.boolean().optional(),
    /** Courier dropoff instructions (`deliveries[0].delivery_notes`). */
    deliveryNotes: z.string().optional(),
    /** Marketplace facilitator tax in CENTS (`payment.tax_reporting.marketplace_facilitator_tax`). */
    marketplaceFacilitatorTaxCents: z.number().int().nonnegative().optional(),
});

export const OrderDataSchema = z.object({
    docId: z.string().optional(),
    orderId: z.number().int().positive(),
    orderNumber: z.number().int().positive().optional(),
    guid: z.string().min(1),
    restaurantId: z.number().int().positive(),
    tenantId: z.number().int().positive(),
    customerId: z.string().optional(),
    customerName: z.string().optional(),
    customerPhone: z.string().optional(),
    customerEmail: z.string().email().optional(),
    cartId: z.number().int().positive().optional(),
    items: z.array(OrderItemSchema).optional().default([]),
    tableId: z.string().optional(),
    activeSeatCount: z.number().int().positive().optional(),
    courseStates: OrderCourseStatesSchema.optional(),
    type: OrderTypeSchema.optional().default('dineIn'),
    orderLevelDiscounts: z.array(AppliedDiscountSchema).optional().default([]),
    appliedTaxes: z.array(AppliedTaxSchema).optional().default([]),
    // Tax exemption + waived tax — see OrderSchema.
    taxExemption: TaxExemptionSchema,
    lateAutoGratuity: LateAutoGratuitySchema,
    // Card hold projection + server close stamp — see OrderSchema.
    cardHold: OrderCardHoldSchema,
    closedBy: OrderServerCloseStampSchema,
    closeReason: OrderServerCloseStampSchema,
    waivedTax: WaivedTaxSchema,
    // Calculated totals for order summary
    subtotal: z.number().min(0).optional(),
    tax: z.number().min(0).optional(),
    total: z.number().min(0).optional(),
    // Display-friendly order type
    // The CART's fulfilment, carried onto the order for display. Mirrors
    // `CartOrderType` — `type` above is the kitchen's view of the same fact and
    // already accepts `dineIn` (it is that field's default). Leaving this
    // narrow made `OrderDataSchema.parse` REFUSE every dine-in guest order at
    // `orderService.ts:810` with `Invalid option: expected one of
    // "pickup"|"delivery"` — loudly, which is the good failure mode; the same
    // omission on a Fastify body would have been silently stripped instead.
    orderType: z.enum(['pickup', 'delivery', 'dineIn']).optional(),
    // Employee tracking (harmonized with POS)
    createdByEmployeeId: z.number().int().positive().optional(),
    modifiedByEmployeeId: z.number().int().positive().optional(),
    sentToKitchenByEmployeeId: z.number().int().positive().optional(),
    completedByEmployeeId: z.number().int().positive().optional(),
    assignedEmployeeId: z.number().int().positive().optional(),
    assignedDeviceId: z.string().optional(),
    /**
     * Attribution for a write made by an OrderUp support session. Declared here
     * because a plain `z.object()` STRIPS undeclared keys — leaving it out would
     * delete the actor on every server round-trip while Firestore kept it.
     */
    supportActor: SupportActorSchema.optional(),
    /** The frozen act-as opener (see the model) — declared so a round-trip cannot strip it. */
    supportOpener: SupportActingAsSchema.optional(),
    // Segment-aware tip pooling: content-derived restaurantGuid:date:window id.
    segmentGuid: z.string().optional(),
    scheduledTime: z.coerce.date().optional(),
    fireAt: z.coerce.date().optional(),
    // Who takes item stock off the shelf (models fireRules `serverTakesStockAtSend`).
    stockCommit: z.literal("atSend").optional(),
    // docs/DYNAMIC_PREP_TIME_ARCHITECTURE.md §5 — the persisted promise.
    promisedReadyAt: z.coerce.date().optional(),
    prepQuote: z
      .object({
        minutes: z.number(),
        paced: z.boolean(),
        channelBase: z.number(),
        orderBase: z.number(),
        largeOrderAdd: z.number(),
        loadItems: z.number(),
        loadAdd: z.number(),
        busyAdd: z.number(),
        scheduled: z.boolean(),
      })
      .optional(),
    deliveryAddress: z.string().optional(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
    createdByUserId: z.string().min(1),
    tabName: z.string().optional(),
    /** POS service mode — see IOrder.serviceMode. Absent = legacy; unknown = dropped, never a failed parse. */
    serviceMode: OrderServiceModeSchema.optional().catch(undefined),
    customerNotes: z.string().optional(),
    internalNotes: z.string().optional(),
    synced: z.boolean().optional().default(false),
    canceledAt: z.coerce.date().optional(),
    canceledBy: z.string().optional(),
    voidedAt: z.coerce.date().optional(),
    voidedBy: z.string().optional(),
    // Three-axis order state (new)
    orderState: OrderStateSchema.optional().default('draft'),
    kitchenState: KitchenStateSchema.optional().default('none'),
    closedAt: z.coerce.date().optional(),
    // The reopen stamp (docs/PAYMENT_REVERSAL_ARCHITECTURE.md §Reopen on void).
    // Declared because a plain `z.object()` STRIPS undeclared keys: an API
    // round-trip would otherwise erase why a closed order is open again.
    reopenedAt: z.coerce.date().optional(),
    reopenReason: z.enum(['payment_voided']).optional(),
    reopenedByPaymentGuid: z.string().optional(),
    voidInfo: OrderVoidInfoSchema.optional(),
    isOverdue: z.boolean().optional(),
    syncState: OrderSyncStateSchema.optional().default('localOnly'),
    syncPriority: OrderSyncPrioritySchema.optional().default('normal'),
    lastSyncAttempt: z.coerce.date().optional(),
    syncRetryCount: z.number().int().min(0).optional().default(0),
    startedAt: z.coerce.date().optional(),
    sentToKitchenAt: z.coerce.date().optional(),
    requiresFirestoreSync: z.boolean().optional().default(false),
    isLocalOnly: z.boolean().optional().default(false),
    // Denormalized owning tenant/restaurant GUIDs — stamped at creation so
    // notification triggers (which forward to the ingest API resolving BY GUID)
    // never fall back to unresolvable docIds. See IOrder.tenantGuid JSDoc.
    tenantGuid: z.string().optional(),
    restaurantGuid: z.string().optional(),
    /**
     * WHICH BRAND took this order — see `OrderSchema.brandGuid`.
     *
     * Declared HERE as well, because THIS is the schema `orderService` parses
     * with before writing (orderService.ts, `OrderDataSchema.parse`, whose
     * result alone reaches the document). Declaring it on `OrderSchema` only
     * is what deleted the brand from every order ever written: zod strips an
     * undeclared key, the write answered 200, and a ghost kitchen's whole
     * order stream arrived undifferentiated while the chit renderer, the
     * ingest and `IOrderData` were all correct.
     *
     * The two schemas must move together. The ingest orchestrator's own
     * key-set guard is derived from `OrderSchema.shape`, so an asymmetry
     * leaves the guard protecting a field the persistence path is removing.
     */
    brandGuid: z.string().optional(),
    /** The brand's name as displayed at order time — see `IOrder.brandName`. */
    brandName: z.string().optional(),
    /** See `OrderIdReassignmentSchema` — declared on both schemas (the write path parses THIS one). */
    orderIdReassignment: OrderIdReassignmentSchema.optional(),
    // Order source tracking
    orderSource: OrderSourceSchema.optional(),
    /** Set when a printed guest QR code sent the guest here. See IOrder.attribution JSDoc. */
    attribution: OrderAttributionSchema.optional(),
    externalOrderId: z.string().optional(),
    externalSource: ExternalOrderSourceSchema.optional(),
    /** DRIVE/COURIER external delivery id — set by DeliveryDispatchService on accept. */
    externalDeliveryId: z.string().optional(),
    /** Courier provider for in-flight Drive delivery. */
    courierProvider: z
        // ⚠️ ADAPTER identity (D1), not marketplace attribution: this records who
        // we DISPATCH THROUGH. Must match `Restaurant.settings.courierProvider`
        // minus its `'none'` sentinel — an order that HAS a courier was
        // dispatched to one. Omitting `'kitchenhub'` made the only value an
        // aggregator dispatch writes the one value this enum rejected.
        .enum(['ubereats', 'doordash', 'grubhub', 'kitchenhub', 'uber_direct'])
        .optional(),
    /** Customer-facing delivery fee charged at checkout, in cents. See IOrder.customerDeliveryFeeCents. */
    customerDeliveryFeeCents: z.number().int().nonnegative().optional(),
    /** Merchant Drive courier cost in cents (server-side only). See IOrder.merchantDriveCostCents. */
    merchantDriveCostCents: z.number().int().nonnegative().optional(),
    /** Provider-neutral courier fields (Uber Direct spec 2026-10-27 §6.4). See IOrder JSDoc. */
    courierQuoteId: z.string().optional(),
    courierQuoteExpiresAt: z.string().optional(),
    merchantCourierCostCents: z.number().int().nonnegative().optional(),
    courierLocation: z.object({ lat: z.number(), lng: z.number() }).optional(),
    courierDispatchFailure: z
        .object({ reason: z.string(), detail: z.string().optional(), at: z.string(), notifiedAt: z.string().optional() })
        .optional(),
    /** Live courier tracking (Drive §7.2) — see IOrder JSDoc. */
    courierStatus: z.string().optional(),
    dasherLocation: z.object({ lat: z.number(), lng: z.number() }).optional(),
    eta: z.string().optional(),
    courierStatusUpdatedAt: z.string().optional(),
    trackingUrl: z.string().optional(),
    courierName: z.string().optional(),
    courierPhone: z.string().optional(),
    /** PIN for the courier's anonymized line (Uber `phone.pin_code`). */
    courierPhonePin: z.string().optional(),
    /** Dasher vehicle (make/model/year) from the courier-status webhook. */
    dasherVehicle: z
        .object({
            make: z.string().optional(),
            model: z.string().optional(),
            year: z.string().optional(),
        })
        .optional(),
    /** Structured dropoff address components for Drive §5.2 dispatch. */
    deliveryAddressComponents: OrderDeliveryAddressComponentsSchema.optional(),
    /** Synthetic management test order — see IOrder.isTest. */
    isTest: z.boolean().optional(),
    /** Canonical courier milestones already notified to the customer — see IOrder.notifiedCourierMilestones. */
    notifiedCourierMilestones: z.array(z.string()).optional(),

    // Check splitting & transfer fields — reference array (checkId or checkGuid)
    checks: z.array(z.union([z.string(), z.number()])).optional().default([]),
    activeCheckCount: z.number().int().nonnegative().optional(),
    totalServiceCharges: z.number().min(0).optional().default(0),
    totalServiceTax: z.number().min(0).optional().default(0),
    onlineServiceFee: OnlineServiceFeeLineSchema.optional(),
    onlineOrderFeeProgram: OnlineOrderFeeProgramSchema.optional(),
    splitStatus: OrderSplitStatusSchema.optional().default('unsplit'),
    hasReceivedTransfers: z.boolean().optional(),
    hasOutgoingTransfers: z.boolean().optional(),
    // Uber Eats marketplace-specific ingest fields — see IOrder JSDoc.
    customerPhonePin: z.string().optional(),
    cutleryRequested: z.boolean().optional(),
    deliveryNotes: z.string().optional(),
    marketplaceFacilitatorTaxCents: z.number().int().nonnegative().optional(),
});

export type OrderSchemaType = z.infer<typeof OrderSchema>;
export type OrderDataSchemaType = z.infer<typeof OrderDataSchema>;
